import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, Fuel, Shield, Award, Users, Clock, MapPin, Star, Store, Droplets, Wrench, Truck } from 'lucide-react';
import { companyInfo, services } from '../data/mock';
import { useTranslation } from 'react-i18next';

const Home = () => {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoadingReviews(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/reviews`);
      const data = await response.json();
      
      if (data.success) {
        // Get the latest 6 reviews for home page
        setReviews(data.reviews.slice(0, 6));
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoadingReviews(false);
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }).map((_, index) => (
      <Star
        key={index}
        className={`w-4 h-4 ${
          index < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
        }`}
      />
    ));
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_ganja-fuel-stations/artifacts/zobl0kp4_IMG_2471.jpeg"
            alt="LUKOIL EKTO"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        </div>
        
        <div className="container mx-auto px-4 z-10 text-white">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-red-600 px-6 py-3 rounded-lg font-bold text-2xl">
                LUKOIL
              </div>
              <div className="text-red-500 font-bold text-3xl">
                EKTO
              </div>
              <div className="hidden md:block h-14 w-px bg-gradient-to-b from-red-400 via-red-500 to-red-600 mx-3 opacity-60"></div>
              <div className="hidden md:block">
                <div 
                  className="text-red-500 text-2xl font-bold tracking-wide"
                  style={{
                    textShadow: '0 2px 8px rgba(220, 38, 38, 0.6), 0 0 20px rgba(239, 68, 68, 0.4)',
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >
                  {t('hero.slogan')}
                </div>
              </div>
            </div>
            {/* Mobile slogan - shown below logo on small screens */}
            <div className="md:hidden mb-5">
              <div 
                className="text-red-500 text-xl font-bold tracking-wide"
                style={{
                  textShadow: '0 2px 6px rgba(220, 38, 38, 0.5), 0 0 15px rgba(239, 68, 68, 0.3)',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                {t('hero.slogan')}
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              {t('hero.title')}
            </h1>
            <p className="text-xl mb-8 text-gray-200 leading-relaxed">
              {t('hero.description')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/stations">
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg">
                  {t('hero.stationsBtn')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg">
                  {t('hero.contactBtn')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-red-600 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">10</div>
              <div className="text-red-100">{t('stats.stations')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">24/7</div>
              <div className="text-red-100">{t('stats.service')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">15+</div>
              <div className="text-red-100">{t('stats.experience')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">100%</div>
              <div className="text-red-100">{t('stats.quality')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">{t('about.title')}</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {t('about.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{t('values.quality.title')}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{t('values.quality.description')}</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{t('values.reliability.title')}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{t('values.reliability.description')}</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Fuel className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{t('values.innovation.title')}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{t('values.innovation.description')}</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{t('values.satisfaction.title')}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{t('values.satisfaction.description')}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">{t('services.title')}</h2>
            <p className="text-lg text-gray-600">{t('services.subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => (
              <Card key={service.id} className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-3">
                    {service.id === 1 && t('services.fuel.title')}
                    {service.id === 2 && t('services.market.title')}
                    {service.id === 3 && t('services.carwash.title')}
                    {service.id === 4 && t('services.technical.title')}
                    {service.id === 5 && t('services.special.title')}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                    {service.id === 1 && t('services.fuel.description')}
                    {service.id === 2 && t('services.market.description')}
                    {service.id === 3 && t('services.carwash.description')}
                    {service.id === 4 && t('services.technical.description')}
                    {service.id === 5 && t('services.special.description')}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white">
                {t('services.viewAll')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">{t('reviews.title')}</h2>
            <p className="text-lg text-gray-600">{t('reviews.subtitle')}</p>
          </div>

          {loadingReviews ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
              <p className="text-gray-600">{t('reviews.loading')}</p>
            </div>
          ) : reviews.length === 0 ? (
            <div className="max-w-2xl mx-auto text-center">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-12">
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">
                    {t('reviews.noReviews')}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {t('reviews.beFirst')}
                  </p>
                  <Link to="/reviews">
                    <Button className="bg-red-600 hover:bg-red-700 text-white">
                      {t('reviews.writeReview')}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
                {reviews.map((review) => (
                  <Card key={review.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="p-6">
                      {/* Rating */}
                      <div className="flex items-center gap-2 mb-4">
                        <div className="flex">{renderStars(review.rating)}</div>
                        <span className="text-sm text-gray-500">
                          ({review.rating}/5)
                        </span>
                      </div>

                      {/* Comment */}
                      <p className="text-gray-700 leading-relaxed mb-4 text-sm line-clamp-4">
                        "{review.comment}"
                      </p>

                      {/* Author */}
                      <div className="flex items-center gap-3 pt-4 border-t">
                        <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-red-600 font-semibold text-lg">
                            {review.name.charAt(0)}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{review.name}</p>
                          <p className="text-xs text-gray-500">{t('reviews.verifiedCustomer')}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="text-center mt-12">
                <Link to="/reviews">
                  <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white">
                    {t('reviews.viewAll')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">{t('cta.title')}</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-red-100">
            {t('cta.description')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/stations">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-red-600 px-8 py-6 text-lg">
                <MapPin className="mr-2 w-5 h-5" />
                {t('cta.nearestStation')}
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg">
                {t('cta.contactUs')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
